// src/pages/InvitationManager.jsx
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Table, Badge, Button, Spinner, Modal, Form, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

// ── Design tokens ─────────────────────────────────────────────────────────────
const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

const ROLE_LABEL = { agent: 'Agente', user: 'Usuario', authorizer: 'Autorizador', leader: 'Líder de área' };
const ROLE_COLOR = { agent: '#2563eb', user: '#6b7280', authorizer: '#0891b2', leader: '#7c3aed' };
const ROLE_BG    = { agent: '#eff6ff', user: '#f3f4f6', authorizer: '#ecfeff', leader: '#f5f3ff' };

function invitationStatus(inv) {
    if (inv.used) return { label: 'Usada', color: '#059669', bg: '#ecfdf5' };
    if (new Date(inv.expiresAt) < new Date()) return { label: 'Expirada', color: 'var(--c-text-3)', bg: '#f3f4f6' };
    return { label: 'Pendiente', color: '#d97706', bg: '#fffbeb' };
}

function Pill({ label, color, bg }) {
    return (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, background: bg, color, fontWeight: 600, fontSize: 11 }}>
            {label}
        </span>
    );
}

// ── Modal nueva invitación ────────────────────────────────────────────────────
function InviteModal({ onClose, onSent, userRole }) {
    const isAdmin  = userRole === 'admin';
    const isLeader = userRole === 'leader';
    const canInviteAgent = isAdmin || isLeader;

    const [email,   setEmail]   = useState('');
    const [role,    setRole]    = useState('agent');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        const emailTrim = email.trim();
        if (!emailTrim) { toast.warn('El email es obligatorio'); return; }
        setSending(true);
        try {
            const endpointMap = {
                agent:      '/auth/invite',
                user:       '/auth/invite-user',
                authorizer: '/auth/invite-authorizer',
                leader:     '/auth/invite-leader',
            };
            await api.post(endpointMap[role] || '/auth/invite-user', { email: emailTrim });
            toast.success(`Invitación enviada a ${emailTrim}`);
            onSent(); onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar la invitación');
        } finally { setSending(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontWeight: 700, fontSize: 16 }}>Nueva invitación</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Correo electrónico</Form.Label>
                    <Form.Control type="email" placeholder="correo@ejemplo.com" value={email}
                        onChange={e => setEmail(e.target.value)} autoFocus style={{ borderRadius: 8 }} />
                </Form.Group>
                <Form.Group>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Rol</Form.Label>
                    <Form.Select value={role} onChange={e => setRole(e.target.value)} style={{ borderRadius: 8 }}>
                        {canInviteAgent && <option value="agent">Agente</option>}
                        {isAdmin && <option value="leader">Líder de área</option>}
                        {isAdmin && <option value="authorizer">Usuario Autorizador</option>}
                    </Form.Select>
                    <Form.Text className="text-muted" style={{ fontSize: 12 }}>
                        {role === 'agent'      ? 'Podrá gestionar y responder tickets.'
                            : role === 'leader'     ? 'Supervisará el área y gestionará agentes de su departamento.'
                            : 'Podrá aprobar o rechazar tickets asignados a él.'}
                    </Form.Text>
                    <Form.Text style={{ fontSize: 12, color: 'var(--c-text-4)', display: 'block', marginTop: 6 }}>
                        Los usuarios con perfil "Usuario" se registran automáticamente con sus credenciales del intranet.
                    </Form.Text>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={sending} style={{ borderRadius: 8 }}>Cancelar</Button>
                <Button variant="primary" onClick={handleSend} disabled={sending} style={{ borderRadius: 8 }}>
                    {sending ? <Spinner size="sm" animation="border" /> : 'Enviar invitación'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function InvitationManager() {
    const { user } = useContext(AuthContext);
    const [invitations, setInvitations] = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState('');
    const [showModal,   setShowModal]   = useState(false);
    const [cancelling,  setCancelling]  = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/auth/invitations');
            setInvitations(data);
        } catch { toast.error('No se pudieron cargar las invitaciones'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCancel = async (id) => {
        if (!window.confirm('¿Cancelar esta invitación?')) return;
        setCancelling(id);
        try {
            await api.delete(`/auth/invitations/${id}`);
            setInvitations(prev => prev.filter(i => i._id !== id));
            toast.success('Invitación cancelada');
        } catch { toast.error('Error al cancelar'); }
        finally { setCancelling(null); }
    };

    const filtered = invitations.filter(inv => inv.email.toLowerCase().includes(search.toLowerCase()));
    const pending  = invitations.filter(i => !i.used && new Date(i.expiresAt) > new Date()).length;
    const used     = invitations.filter(i => i.used).length;
    const expired  = invitations.filter(i => !i.used && new Date(i.expiresAt) <= new Date()).length;

    const kpis = [
        { label: 'Pendientes', value: pending,            color: '#d97706' },
        { label: 'Usadas',     value: used,               color: '#059669' },
        { label: 'Expiradas',  value: expired,            color: 'var(--c-text-3)' },
        { label: 'Total',      value: invitations.length, color: '#2563eb' },
    ];

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h4 style={{ fontWeight: 700, margin: 0, color: 'var(--c-text-1)' }}>Invitaciones</h4>
                    <p style={{ color: 'var(--c-text-3)', margin: '4px 0 0', fontSize: 14 }}>Envía y administra invitaciones de agentes y usuarios</p>
                </div>
                <Button variant="success" onClick={() => setShowModal(true)} style={{ borderRadius: 8 }}>
                    + Nueva invitación
                </Button>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
                {kpis.map(k => (
                    <div key={k.label} className="card-hover" style={{ ...CARD, borderTop: `4px solid ${k.color}`, padding: '16px 20px' }}>
                        <p style={LABEL}>{k.label}</p>
                        <p style={{ fontSize: 32, fontWeight: 800, color: 'var(--c-text-1)', margin: '4px 0 0', lineHeight: 1 }}>{k.value}</p>
                    </div>
                ))}
            </div>

            {/* Search + Table */}
            <div style={CARD}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <InputGroup style={{ maxWidth: 320 }}>
                        <Form.Control
                            placeholder="Buscar por email…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ borderRadius: '8px 0 0 8px', fontSize: 13 }}
                        />
                        {search && (
                            <Button variant="outline-secondary" onClick={() => setSearch('')} style={{ borderRadius: '0 8px 8px 0' }}>✕</Button>
                        )}
                    </InputGroup>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '48px 0' }}><Spinner animation="border" /></div>
                ) : filtered.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--c-text-4)', padding: '48px 0', margin: 0 }}>No hay invitaciones que coincidan.</p>
                ) : (
                    <Table hover responsive className="mb-0 align-middle" style={{ fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: 'var(--c-msg-bg)' }}>
                                <th style={{ padding: '10px 20px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>Email</th>
                                <th style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>Rol</th>
                                <th style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>Estado</th>
                                <th style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>Expira</th>
                                <th style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)' }}>Enviada</th>
                                <th style={{ padding: '10px 16px', color: 'var(--c-text-3)', fontWeight: 600, borderBottom: '1px solid var(--c-border)', textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(inv => {
                                const st = invitationStatus(inv);
                                return (
                                    <tr key={inv._id}>
                                        <td style={{ padding: '12px 20px', fontWeight: 600, color: 'var(--c-text-1)' }}>{inv.email}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <Pill label={ROLE_LABEL[inv.role] || inv.role} color={ROLE_COLOR[inv.role] || '#6b7280'} bg={ROLE_BG[inv.role] || '#f3f4f6'} />
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <Pill label={st.label} color={st.color} bg={st.bg} />
                                        </td>
                                        <td style={{ padding: '12px 16px', color: 'var(--c-text-3)' }}>
                                            {new Date(inv.expiresAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td style={{ padding: '12px 16px', color: 'var(--c-text-3)' }}>
                                            {new Date(inv.createdAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                            {!inv.used ? (
                                                <Button size="sm" variant="outline-danger"
                                                    disabled={cancelling === inv._id}
                                                    onClick={() => handleCancel(inv._id)}
                                                    style={{ borderRadius: 8 }}
                                                >
                                                    {cancelling === inv._id ? <Spinner size="sm" animation="border" /> : 'Cancelar'}
                                                </Button>
                                            ) : (
                                                <span style={{ color: 'var(--c-text-4)', fontSize: 12 }}>—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </div>

            {showModal && <InviteModal onClose={() => setShowModal(false)} onSent={load} userRole={user?.role} />}
        </div>
    );
}
